import moment from 'moment-timezone';

export const isValidTimezone = (value: unknown): value is string => {
  if (typeof value !== 'string' || !moment.tz.zone(value)) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

export const timezoneOptions = moment.tz.names().filter(isValidTimezone);

// Compare aliases, not today's UTC offset: distinct zones can diverge in winter.
export const sameTimezone = (left: string, right: string) =>
  isValidTimezone(left) &&
  isValidTimezone(right) &&
  new Intl.DateTimeFormat('en', { timeZone: left }).resolvedOptions()
    .timeZone ===
    new Intl.DateTimeFormat('en', { timeZone: right }).resolvedOptions()
      .timeZone;

// Existing records may use different IANA names with equivalent clock rules.
// Check the coming year, rather than only today's offset, to catch seasonal errors.
export const haveSameUpcomingClockTimes = (
  left: string,
  right: string,
  from = new Date(),
) => {
  if (!isValidTimezone(left) || !isValidTimezone(right)) return false;
  if (sameTimezone(left, right)) return true;
  const formatter = (timeZone: string) =>
    new Intl.DateTimeFormat('en', { timeZone, timeZoneName: 'longOffset' });
  const leftFormatter = formatter(left);
  const rightFormatter = formatter(right);
  const offset = (format: Intl.DateTimeFormat, instant: number) =>
    format.formatToParts(instant).find((part) => part.type === 'timeZoneName')
      ?.value;
  const sixHours = 6 * 60 * 60 * 1000;
  for (let sample = 0; sample <= 366 * 4; sample += 1) {
    const instant = from.getTime() + sample * sixHours;
    if (offset(leftFormatter, instant) !== offset(rightFormatter, instant))
      return false;
  }
  return true;
};

export const coordinateNumber = (value: unknown): number =>
  typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== ''
    ? Number(value)
    : NaN;

export const isValidLatitude = (value: unknown) => {
  const number = coordinateNumber(value);
  return Number.isFinite(number) && number >= -90 && number <= 90;
};

export const isValidLongitude = (value: unknown) => {
  const number = coordinateNumber(value);
  return Number.isFinite(number) && number >= -180 && number <= 180;
};

export const isValidCoordinates = (latitude: unknown, longitude: unknown) =>
  isValidLatitude(latitude) &&
  isValidLongitude(longitude) &&
  !(coordinateNumber(latitude) === 0 && coordinateNumber(longitude) === 0);
